import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { VersionStatus } from '@risksmart-app/drizzle/src/types/consts';
import type { DocumentByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetDocumentByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcDocumentToGraphQL(
  document: DocumentByIdResponseRow
): GetDocumentByIdQuery['document'][number] {
  // Filter document files by status for draft and published versions
  const draftVersions =
    document.documentFiles?.filter(
      (file) => file.Status === VersionStatus.Draft
    ) || [];

  const publishedVersions =
    document.documentFiles?.filter(
      (file) => file.Status === VersionStatus.Published
    ) || [];

  return {
    ...document,
    latestDraftVersion: draftVersions,
    latestPublishedVersion: publishedVersions,
  };
}

/**
 * Maps TRPC document data to match the GraphQL query structure
 */
function mapTrpcDocumentToGraphQLQuery(
  trpcData: DocumentByIdResponseRow[] | undefined
): GetDocumentByIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    document: trpcData.map(mapTrpcDocumentToGraphQL),
  };
}

export const useGetDocumentById = (documentId: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.document.documentById.queryOptions({ documentId }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcDocumentToGraphQLQuery(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
